import { Action, Location, State, To } from '../../types';

import { getNextLocation } from './shared';
import { HistoryBackend, Notify } from './types';

export interface MemoryHistoryOptions {
    /** Path the in-memory stack starts at. Default '/'. */
    initialPath?: string;
}

/**
 * Memory backend: an in-memory entries stack with a current index. Never touches
 * window.location or window.history, so the SDK can run as a transient/modal embed
 * without polluting the host page's URL or Back button. back()/forward() move the
 * index and emit POP. The routing layer reads this backend's location via a
 * location source wired up in the app layer (createSDK) — this module deliberately
 * does not import @pressreader/routing.
 */
export function createMemoryBackend(notify: Notify, options: MemoryHistoryOptions = {}): HistoryBackend {
    let entries: Location[] = [getNextLocation(options.initialPath ?? '/')];
    let index = 0;

    return {
        kind: 'memory',

        push(to: To, state: State = {}): void {
            const next = getNextLocation(to, state);
            // Drop any forward entries, then append (mirrors browser push semantics).
            entries = entries.slice(0, index + 1);
            entries.push(next);
            index = entries.length - 1;
            notify('PUSH');
        },

        replace(to: To, state: State = {}, action: Action = 'REPLACE'): void {
            // Same entry, new content: carry the entry identity.
            entries[index] = getNextLocation(to, state, entries[index].entryKey);
            notify(action);
        },

        getLocation(): Location {
            return entries[index];
        },

        go(delta: number): void {
            const target = index + delta;
            if (target < 0 || target >= entries.length) {
                return; // out of range — no-op, like a browser at the ends of its history
            }
            index = target;
            notify('POP');
        },

        dispose(): void {
            entries = [];
            index = 0;
        },
    };
}
